import React, { useState } from 'react';
import classNames from 'classnames/bind';

import styles from './DatePicker.pcss';

const cx = classNames.bind(styles);

const DatePicker = ({
  onDateSelect,
  setShowCalendar,
  preventDefaultPropagation,
  skipUpdateOncancel = false,
  isEditProfile = false
}) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [showYears, setShowYears] = useState(false);

  const toggleShowYears = () => {
    setShowYears(!showYears);
  };

  const onCancelClick = () => {
    !skipUpdateOncancel && onDateSelect(new Date());
    setShowCalendar(false);
  };

  const okClick = () => {
    onDateSelect(selectedDate);
    setShowCalendar(false);
  };

  // Function to handle year change
  const handleYearChange = event => {
    const year = parseInt(event.target.value);
    const newDate = new Date(selectedDate);
    newDate.setFullYear(year);
    setSelectedDate(newDate);
  };

  // Function to handle month change
  const changeMonth = (event, increment) => {
    event.stopPropagation();

    if (isEditProfile) {
      event.preventDefault();
    }

    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() + increment);

    // Get current year and month
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();

    // Check if the new month is in the future
    if (
      newDate.getFullYear() > currentYear ||
      (newDate.getFullYear() === currentYear && newDate.getMonth() > currentMonth)
    ) {
      return;
    }
    setSelectedDate(newDate);
  };

  // Function to generate years from current year minus 100
  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= currentYear - 100; year--) {
      years.push(year);
    }
    return years;
  };

  const getCalendarDays = () => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    const currentDay = new Date().getDate();
    const daysInMonth = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth() + 1,
      0
    ).getDate();
    const firstDayIndex = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1).getDay();

    const days = [];

    // Previous month days
    for (let i = firstDayIndex; i > 0; i--) {
      days.push(
        <div className={cx('day', 'disabled')} key={`prev-${i}`} aria-disabled={true}>
          {''}
        </div>
      );
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      const currentDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), i);
      const selectedClass = selectedDate && selectedDate.getDate() === i ? 'selected' : '';
      const disabledClass =
        currentDate > new Date(currentYear, currentMonth, currentDay) ? 'disabled' : '';
      days.push(
        <div
          role="gridcell"
          className={cx('day', `${selectedClass}`, `${disabledClass}`)}
          key={`current-${i}`}
          onClick={() =>
            currentDate <= new Date(currentYear, currentMonth, currentDay) && handleDateSelect(i)
          }
          aria-selected={selectedDate && selectedDate.getDate() === i}
          aria-disabled={currentDate > new Date(currentYear, currentMonth, currentDay)}
        >
          {i}
        </div>
      );
    }

    // Next month days
    const remainingDays = 7 - (days.length % 7);
    for (let i = 1; i <= remainingDays; i++) {
      days.push(
        <div className={cx('day', 'disabled')} key={`next-${i}`} aria-disabled={true}>
          {''}
        </div>
      );
    }

    return days;
  };

  const handleDateSelect = day => {
    const currentDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day);
    const today = new Date();
    if (currentDate <= today) {
      const selectedDateCopy = new Date(selectedDate);
      selectedDateCopy.setDate(day);
      setSelectedDate(selectedDateCopy);
    }
  };

  const isNextArrowDisabled = () => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();

    // Check if the next month is in the future
    return (
      selectedDate.getFullYear() > currentYear ||
      (selectedDate.getFullYear() === currentYear && selectedDate.getMonth() > currentMonth)
    );
  };

  return (
    <div className={cx('date-picker')} onClick={e => e.stopPropagation()}>
      <div className={cx('first-row')}>
        <div className={cx('year-dropdown')}>
          <select onChange={handleYearChange}>
            {generateYears().map(year => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <div 
          className={cx('selected-date')}
          aria-label={selectedDate.toLocaleDateString('en-US', {
            weekday: 'long', 
            day: 'numeric', 
            month: 'long',  
          })}
        >
          <span>
            {selectedDate.toLocaleDateString('en-US', { weekday: 'short' })}{' '}
            {selectedDate.getDate()}, {selectedDate.toLocaleDateString('en-US', { month: 'short' })}
          </span>
        </div>
      </div>
      <div className={cx('month-navigation')}>
        <button onClick={event => changeMonth(event, -1)} aria-label="Previous month">&lt;</button>
        <h2
          aria-label={selectedDate.toLocaleDateString('default', {
            month: 'long',   
            year: 'numeric', 
          })}  
        >{selectedDate.toLocaleDateString('default', { month: 'long', year: 'numeric' })}</h2>
        <button
          className={cx(isNextArrowDisabled() ? 'disabled' : '')}
          onClick={e => changeMonth(e, 1)}
          aria-disabled={isNextArrowDisabled()}
          aria-label="Next month"
        >
          &gt;
        </button>
      </div>
      <div className={cx('calendar')}>
        <div className={cx('days')} role='grid'>
          <div className={cx('day-name')} aria-label="Sunday">S</div>
          <div className={cx('day-name')} aria-label="Monday">M</div>
          <div className={cx('day-name')} aria-label="Tuesday">T</div>
          <div className={cx('day-name')} aria-label="Wednesday">W</div>
          <div className={cx('day-name')} aria-label="Thursday">T</div>
          <div className={cx('day-name')} aria-label="Friday">F</div>
          <div className={cx('day-name')} aria-label="Saturday">S</div>
          {getCalendarDays()}
        </div>
      </div>
      <div className={cx('ok-cancel')}>
        <div role="button" className={cx('ok')} onClick={() => okClick()}>
          OK
        </div>
        <div role="button" className={cx('cancel')} onClick={() => onCancelClick()}>
          Cancel
        </div>
      </div>
    </div>
  );
};

export default DatePicker;
